import { Bell, Menu } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { setSidebarOpen } from '../../store/adminSlice'
import type { AppDispatch } from '../../store'
import { api } from '../../lib/api'
import {
  playNotificationSound,
  useAdminPushNotifications,
  type AdminPushPayload,
} from '../../hooks/useAdminPushNotifications'

type AdminNotification = {
  id: number
  title: string
  message: string
  type?: string
  deepLink?: string
  unread: boolean
  createdAt: string
}

type AdminToast = AdminPushPayload & {
  id: string
  exiting?: boolean
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function shouldAlertAdminNotification(notification: Pick<AdminNotification, 'title' | 'type'>) {
  const title = notification.title.toLowerCase()
  return notification.type === 'broadcast' ||
    title.includes('new order received') ||
    title.includes('new order rating') ||
    title.includes('feedback')
}

function routeFromNotificationUrl(value?: string) {
  if (!value) return '/notifications'
  try {
    if (value.startsWith('http')) {
      const url = new URL(value)
      return `${url.pathname}${url.search}`
    }
  } catch {
    return value
  }
  return value
}

function orderIdFromRoute(value?: string) {
  if (!value) return null
  const match = routeFromNotificationUrl(value).match(/^\/orders\/(\d+)/)
  return match ? Number(match[1]) : null
}

function isOrderRelatedNotification(payload: { url?: string; notificationType?: string; type?: string; title?: string; deepLink?: string }) {
  return payload.notificationType === 'order' ||
    payload.type === 'order' ||
    routeFromNotificationUrl(payload.url || payload.deepLink).startsWith('/orders') ||
    (payload.title || '').toLowerCase().includes('order')
}

function notifyOrderScreens(payload: { url?: string; notificationType?: string; type?: string; title?: string; deepLink?: string }) {
  if (!isOrderRelatedNotification(payload)) return
  const orderId = orderIdFromRoute(payload.url || payload.deepLink)
  window.dispatchEvent(new CustomEvent('seasonly-admin-orders-refresh', { detail: { orderId } }))
  window.dispatchEvent(new CustomEvent('seasonly-admin-products-refresh', { detail: { orderId } }))
  if (orderId) {
    window.dispatchEvent(new CustomEvent('seasonly-admin-order-refresh', { detail: { orderId } }))
  }
}

export function Header() {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const notificationRef = useRef<HTMLDivElement | null>(null)
  const toastDragStartRef = useRef<Record<string, number>>({})
  const toastSwipedRef = useRef<Record<string, boolean>>({})
  const recentToastKeysRef = useRef<Record<string, number>>({})
  const streamReadyRef = useRef(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [notifications, setNotifications] = useState<AdminNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [toasts, setToasts] = useState<AdminToast[]>([])
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const dismissToast = useCallback((toastId: string) => {
    setToasts(items => items.map(item =>
      item.id === toastId ? { ...item, exiting: true } : item,
    ))
    window.setTimeout(() => {
      setToasts(items => items.filter(item => item.id !== toastId))
    }, 320)
  }, [])
  const addToast = useCallback((payload: AdminPushPayload) => {
    const toastKey = `${payload.url || ''}|${payload.title}|${payload.message}`
    const now = Date.now()
    if (recentToastKeysRef.current[toastKey] && now - recentToastKeysRef.current[toastKey] < 2500) {
      return
    }
    recentToastKeysRef.current[toastKey] = now

    const toastId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    setToasts(items => [{ ...payload, id: toastId }, ...items].slice(0, 5))
    window.setTimeout(() => dismissToast(toastId), 6500)
  }, [dismissToast])
  const loadNotifications = useCallback(() => {
    return api.get('/admin/notifications?limit=5')
      .then(({ data }) => {
        const nextNotifications = data.notifications ?? []
        const latest = nextNotifications[0] as AdminNotification | undefined
        const lastSeenId = Number(localStorage.getItem('adminLastNotificationId') || 0)

        if (
          latest?.id &&
          initialLoadDone &&
          latest.id > lastSeenId &&
          latest.unread &&
          shouldAlertAdminNotification(latest)
        ) {
          addToast({
            title: latest.title,
            message: latest.message,
            url: latest.deepLink,
            notificationType: latest.type,
          })
          notifyOrderScreens(latest)
          playNotificationSound()
        }

        if (latest?.id) localStorage.setItem('adminLastNotificationId', String(latest.id))
        setNotifications(nextNotifications)
        setUnreadCount(data.unread ?? 0)
        setInitialLoadDone(true)
      })
      .catch(() => undefined)
  }, [addToast, initialLoadDone])
  useAdminPushNotifications((payload) => {
    if (!shouldAlertAdminNotification({ title: payload.title, type: payload.notificationType })) return
    addToast(payload)
    notifyOrderScreens(payload)
    loadNotifications()
  })

  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  useEffect(() => {
    const token = localStorage.getItem('adminToken')
    if (!token) return undefined

    const apiBase = import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api'
    const streamUrl = `${apiBase}/admin/notifications/stream?token=${encodeURIComponent(token)}`
    const source = new EventSource(streamUrl)

    source.addEventListener('admin-notification', (event) => {
      const payload = JSON.parse((event as MessageEvent).data) as {
        unread: number
        notification: AdminNotification | null
      }

      const isInitialStreamSync = !streamReadyRef.current
      streamReadyRef.current = true

      setUnreadCount(payload.unread)

      if (payload.notification) {
        setNotifications(items => [
          payload.notification as AdminNotification,
          ...items.filter(item => item.id !== payload.notification?.id),
        ].slice(0, 5))
        if (!isInitialStreamSync && shouldAlertAdminNotification(payload.notification)) {
          addToast({
            title: payload.notification.title,
            message: payload.notification.message,
            url: payload.notification.deepLink,
            notificationType: payload.notification.type,
          })
          notifyOrderScreens(payload.notification)
          playNotificationSound()
        }
        localStorage.setItem('adminLastNotificationId', String(payload.notification.id))
      }
    })

    source.onerror = () => {
      source.close()
    }

    return () => source.close()
  }, [])

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadNotifications()
    }, 8000)

    return () => window.clearInterval(interval)
  }, [loadNotifications])

  useEffect(() => {
    if (!notificationsOpen) return undefined

    const handlePointerDown = (event: PointerEvent) => {
      if (!notificationRef.current?.contains(event.target as Node)) {
        setNotificationsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [notificationsOpen])

  const markRead = async () => {
    setUnreadCount(0)
    setNotifications(items => items.map(item => ({ ...item, unread: false })))
    await api.patch('/admin/notifications/read-all').catch(() => undefined)
  }

  const openNotification = async (notification: AdminNotification) => {
    setNotificationsOpen(false)
    if (notification.unread) {
      setNotifications(items => items.map(item =>
        item.id === notification.id ? { ...item, unread: false } : item,
      ))
      setUnreadCount(count => Math.max(0, count - 1))
      await api.patch(`/admin/notifications/${notification.id}/read`).catch(() => {
        loadNotifications()
      })
    }
    const route = routeFromNotificationUrl(notification.deepLink)
    const orderId = orderIdFromRoute(route)
    navigate(route)
    if (orderId && location.pathname === `/orders/${orderId}`) {
      window.dispatchEvent(new CustomEvent('seasonly-admin-order-refresh', { detail: { orderId } }))
    }
  }

  const openToast = (toast: AdminToast) => {
    if (toastSwipedRef.current[toast.id]) {
      toastSwipedRef.current[toast.id] = false
      return
    }

    dismissToast(toast.id)
    const route = routeFromNotificationUrl(toast.url)
    const orderId = orderIdFromRoute(route)
    navigate(route)
    if (orderId && location.pathname === `/orders/${orderId}`) {
      window.dispatchEvent(new CustomEvent('seasonly-admin-order-refresh', { detail: { orderId } }))
    }
  }

  const handleToastPointerDown = (toastId: string, clientX: number) => {
    toastDragStartRef.current[toastId] = clientX
    toastSwipedRef.current[toastId] = false
  }

  const handleToastPointerUp = (toastId: string, clientX: number) => {
    const startX = toastDragStartRef.current[toastId]
    if (typeof startX !== 'number') return

    if (clientX - startX > 72) {
      toastSwipedRef.current[toastId] = true
      dismissToast(toastId)
    }
  }

  return (
    <header className="sticky top-0 z-20 border-b border-stone-200 bg-[#fbfaf6]/90 px-4 py-3 backdrop-blur lg:px-6">
      {toasts.length ? (
        <div className="fixed right-4 top-4 z-50 flex w-[min(92vw,380px)] flex-col gap-3">
          {toasts.map((toast) => (
            <button
              className={[
                'w-full rounded-xl border border-mango-yellow bg-white p-4 text-left shadow-2xl ring-4 ring-mango-yellow/20 transition-all duration-300',
                toast.exiting ? 'translate-x-[120%] opacity-0' : 'translate-x-0 opacity-100',
              ].join(' ')}
              key={toast.id}
              onClick={() => openToast(toast)}
              onPointerDown={(event) => handleToastPointerDown(toast.id, event.clientX)}
              onPointerUp={(event) => handleToastPointerUp(toast.id, event.clientX)}
              type="button"
            >
              <div className="flex gap-3">
                <span className="mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-mango-yellow/20 text-leaf-dark">
                  <Bell className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-leaf-dark">{toast.title}</p>
                  <p className="mt-1 text-sm leading-5 text-stone-600">{toast.message}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <button
          className="rounded-lg border border-stone-200 bg-white p-2 text-leaf-dark lg:hidden"
          onClick={() => dispatch(setSidebarOpen(true))}
          type="button"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-leaf-dark">Seasonly Admin</p>
          <p className="hidden text-xs text-stone-500 sm:block">Manage mango orders, catalog, customers, and notifications</p>
        </div>
        <div className="relative" ref={notificationRef}>
          <button
            className="relative grid h-11 w-11 place-items-center rounded-lg border border-stone-200 bg-white text-leaf-dark"
            onClick={() => {
              setNotificationsOpen((open) => !open)
            }}
            type="button"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 ? (
              <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : null}
          </button>
          {notificationsOpen ? (
            <div className="absolute right-0 top-12 w-[min(90vw,360px)] rounded-xl border border-stone-200 bg-white p-3 shadow-xl">
              <div className="mb-2 flex items-center justify-between">
                <p className="font-bold text-leaf-dark">Notifications</p>
                <Button onClick={markRead} variant="ghost">Mark read</Button>
              </div>
              <div className="space-y-2">
                {notifications.length === 0 ? (
                  <p className="rounded-lg p-3 text-sm text-stone-500">No admin notifications yet.</p>
                ) : null}
                {notifications.map((notification) => (
                  <button
                    className="flex w-full gap-3 rounded-lg p-2 text-left hover:bg-mango-yellow/10"
                    key={notification.id}
                    onClick={() => openNotification(notification)}
                    type="button"
                  >
                    <span className={notification.unread ? 'mt-2 h-2 w-2 shrink-0 rounded-full bg-mango-orange' : 'mt-2 h-2 w-2 shrink-0 rounded-full bg-stone-200'} />
                    <div>
                      <p className="text-sm font-semibold text-stone-900">{notification.title}</p>
                      <p className="text-xs leading-5 text-stone-500">{notification.message}</p>
                      <p className="mt-1 text-[11px] font-medium text-stone-400">{formatTime(notification.createdAt)}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  )
}
